import React, { useEffect, useRef } from 'react';
import * as Highcharts from 'highcharts';
import { useTranslation } from "react-i18next";

interface HorizontalStackedBarData {
    name: string;
    accepted: number;
    rejected: number;
    systemAccuracy: number;
    humanAccuracy: number;
    showXaxisLabels?: boolean;
}

interface LivisHorizontalStackedBarHighchartsProps {
    data: HorizontalStackedBarData[];
    height?: number;
    showTooltip?: boolean;
    showLabels?: boolean;
    showYaxisLabels?: boolean;
    colors?: {
        accepted: string;
        rejected: string;
    };
    onPointClick?: (point: any) => void;
    pointWidth?: number;
    noDataMessage?: string;
    x_title?: string;
    y_title?: string;
    yAxisConfig?: any;
    emptyDataMessage?: string;
}

const LivisHorizontalStackedBarHighcharts: React.FC<LivisHorizontalStackedBarHighchartsProps> = ({
    data = [],
    height = 400,
    showTooltip = true,
    showLabels = true,
    colors = {
        accepted: '#007BFF',
        rejected: '#DC3545'
    },
    onPointClick,
    pointWidth = 30,
    noDataMessage = `No Data to Display`,
    emptyDataMessage = `No Data to Display`,
    showYaxisLabels = true,
    x_title,
    y_title,
    yAxisConfig
}) => {
    const { t } = useTranslation();
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<Highcharts.Chart | null>(null);

    const hasData = data && data.length > 0;

    // --- Helper: Resolve CSS variables ---
    const resolveColor = (val?: string) => {
        if (!val) return val;
        try {
            const m = String(val).match(/var\((--[a-zA-Z0-9-_]+)\)/);
            if (m) {
                const cssVal = getComputedStyle(document.documentElement).getPropertyValue(m[1]);
                if (cssVal && cssVal.trim()) return cssVal.trim();
            }
        } catch {
            // ignore
        }
        return val;
    };

    // --- No Data Template ---
    const NoDataTemplate = () => (
        <div
            className="livis-dashboard-no-record-centered"
            style={{
                width: '100%',
                height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <p>{t(noDataMessage)}</p>
        </div>
    );

    // --- Chart initialization ---
    useEffect(() => {
        if (!chartRef.current || !hasData) return;

        const chartOptions: Highcharts.Options = {
            chart: {
                type: 'bar',
                height,
                backgroundColor: 'transparent',
                marginLeft: showLabels ? (y_title ? 120 : 100) : 5,
                marginRight: showLabels ? 50: 10,
                marginBottom: x_title ? 60 : 24,
            },
            title: { text: undefined },
            xAxis: {
                categories: data.map(item => item.name),
                labels: {
                    enabled: showLabels,
                    style: {
                        color: resolveColor('var(--livis-gray-font-color)'),
                        fontSize: '14px',
                        fontWeight: '600'
                    },
                    formatter: function () {
                        const fullText = this.value as string;
                        const maxLength = 10;
                        let displayText = fullText;
                        if (fullText.length > maxLength)
                            displayText = `${fullText.substring(0, maxLength)}...`;
                        return `
                            <div title="${fullText}" class="livis-horizontal-stacked-bar-highchart-label-wrap">
                                <div class="livis-horizontal-stacked-bar-highchart-label-text">${displayText}</div>
                            </div>`;
                    },
                    useHTML: true,
                    align: 'right',
                    x: -4
                },
                lineWidth: 0,
                tickWidth: 0,
                gridLineWidth: 0,
                title: x_title
                    ? {
                        text: x_title,
                        align: 'middle',
                        x: 20,
                        y: 0,
                        style: { fontSize: '14px', fontWeight: '600' }
                      } as any
                    : undefined
            },
            yAxis: {
                min: 0,
                max: yAxisConfig?.max || undefined,
                title: y_title ? { text: y_title, style: { fontSize: '14px', fontWeight: '600' } } : { text: undefined },
                labels: {
                    enabled: showYaxisLabels,
                    style: {
                        fontSize: yAxisConfig?.fontSize || '14px',
                    }
                },
                gridLineWidth: 0,
                lineWidth: 0,
                tickWidth: 0
            },
            legend: { enabled: false },
            tooltip: {
                enabled: showTooltip,
                backgroundColor: 'var(--livis-recipe-details-part-footer-color)',
                borderRadius: 8,
                style: {
                    color: 'var(--livis-white-color)'
                },
                useHTML: true,
                formatter: function () {
                    const point = this.point as any;
                    const categoryIndex = point.x;
                    const item = data[categoryIndex || 0];

                    if (!item) return '';

                    const accepted = item.accepted || 0;
                    const rejected = item.rejected || 0;
                    const total = accepted + rejected;
                    const acceptanceRate = total > 0 ? ((accepted / total) * 100).toFixed(1) : '0';
                    const rejectionRate = total > 0 ? ((rejected / total) * 100).toFixed(1) : '0';
                    
                    const hasAccuracyData = item.systemAccuracy != null || item.humanAccuracy != null;
                    
                    if (hasAccuracyData) {
                        const livisAcc = item?.systemAccuracy != null ? `${Number(item.systemAccuracy).toFixed(2)}%` : '--';
                        const actualAcc = item?.humanAccuracy != null ? `${Number(item.humanAccuracy).toFixed(2)}%` : '--';
                        
                        return `
                            <div style="padding:8px; font-family: 'Noto Sans Regular'">
                                <div style="font-weight:700; color:var(--livis-white-color); margin-bottom:8px; font-size:12px;">
                                    ${item.name}
                                </div>
                                <div style="display: flex; gap: 4px; font-weight:600; color:var(--livis-white-color); margin-bottom:4px; font-size:10px;">
                                    ${t('System Accuracy')}: ${livisAcc}
                                </div>
                                <div style="display: flex; gap: 4px; font-weight:600; color:var(--livis-white-color); font-size:10px;">
                                    ${t('Human Accuracy')}: ${actualAcc}
                                </div>
                            </div>
                        `;
                    } else {
                        return `
                            <div style="padding:8px; font-family: 'Noto Sans Regular'">
                                <div style="font-weight:700; color: var(--livis-white-color); margin-bottom:8px; font-size:12px;">
                                    ${item.name}
                                </div>
                                <div style="display: flex; justify-content: space-between; margin-bottom:4px;">
                                    <span style="color:#75DE90; font-size:10px;">${t('Accepted')}:</span>
                                    <span style="color:var(--livis-white-color); font-size:10px; font-weight:600;">${accepted.toLocaleString()}</span>
                                </div>
                                <div style="display: flex; justify-content: space-between; margin-bottom:4px;">
                                    <span style="color:#C43333; font-size:10px;">${t('Rejected')}:</span>
                                    <span style="color:var(--livis-white-color); font-size:10px; font-weight:600;">${rejected.toLocaleString()}</span>
                                </div>
                                <div style="display: flex; justify-content: space-between; margin-bottom:4px;">
                                    <span style="color:var(--livis-white-color); font-size:10px;">${t('Total')}:</span>
                                    <span style="color:var(--livis-white-color); font-size:10px; font-weight:600;">${total.toLocaleString()}</span>
                                </div>
                                <div style="display: flex; justify-content: space-between;">
                                    <span style="color:var(--livis-white-color); font-size:10px;">${t('Acceptance Rate')}:</span>
                                    <span style="color:var(--livis-white-color); font-size:10px; font-weight:600;">${acceptanceRate}%</span>
                                </div>
                            </div>
                        `;
                    }
                }
            },
            plotOptions: {
                bar: {
                    pointWidth,
                    pointPadding: 0.1,
                    borderWidth: 0,
                    borderRadius: 6,
                    groupPadding: 0,
                    dataLabels: {
                        enabled: showLabels,
                        inside: false,
                        align: 'left',
                        x: 5,
                        style: {
                            fontSize: '0.8125rem',
                            fontWeight: '600',
                            textOutline: 'none'
                        },
                        formatter: function () {
                            const labelColor =
                                this.series.name === t('Accepted')
                                    ? resolveColor(colors.accepted)
                                    : resolveColor(colors.rejected);
                            const value = this.y && this.y > 0 ? this.y.toLocaleString() : '';
                            if (!value) return '';
                            return `<span class="livis-horizontal-stacked-bar-highchart-datalabel" style="color: ${labelColor};">${value}</span>`;
                        },
                        useHTML: true
                    },
                    point: {
                        events: {
                            click: function () {
                                if (onPointClick) {
                                    const categoryIndex = this.x;
                                    const clickedItem = data[categoryIndex || 0];
                                    onPointClick(clickedItem);
                                }
                            }
                        }
                    }
                }
            },
            series: [
                {
                    name: t('Accepted') || "",
                    type: 'bar',
                    data: data.map(item => item.accepted),
                    color: resolveColor(colors.accepted)
                },
                {
                    name: t('Rejected') || "",
                    type: 'bar',
                    data: data.map(item => item.rejected),
                    color: resolveColor(colors.rejected)
                }
            ],
            credits: { enabled: false },
            exporting: { enabled: false }
        };

        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        chartInstance.current = Highcharts.chart(chartRef.current, chartOptions);

        return () => {
            chartInstance.current?.destroy();
            chartInstance.current = null;
        };
    }, [data, height, showTooltip, showLabels, colors, onPointClick, pointWidth, hasData, t]);

    if (!hasData) {
        return <NoDataTemplate />;
    }

    if (!data.length) {
        return <div style={{ textAlign: "center", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--livis-color-primary-gray)" }}>{emptyDataMessage}</div>;
    }

    return <div ref={chartRef} style={{ width: '100%', height: `${height}px` }} />;
};

export default LivisHorizontalStackedBarHighcharts;